export function formatINR(value) {
  const num = Number(String(value).replace(/\D/g, ""));
  if (!num) return "₹0";
  return "₹" + num.toLocaleString("en-IN");
}

function compareDiff(a, b) {
  const diff = (+a) - (+b);
  return {
    diff: Math.abs(diff),
    trend: diff > 0 ? "increase" : "decrease",
  };
}

function findDayAfter(allContent, lastDisplayedDate) {
  if (!lastDisplayedDate || !allContent?.length) return null;
  const lastDate = String(lastDisplayedDate).slice(0, 10);
  const matchIndex = allContent.findIndex((d) => String(d?.added_on).slice(0, 10) === lastDate);
  if (matchIndex === -1) return null;
  return allContent[matchIndex + 1] || null;
}

function extractGoldDaysData(data, days = 7) {
  if (!data || !data.length) return null;
  const daysData = data.slice(0, days + 1);

  const rates = [];
  for (let i = 0; i < daysData.length - 1; i++) {
    const curr = daysData[i];
    const prev = daysData[i + 1];
    const c24 = Math.round(curr?.gold_24_carat / 10);
    const { diff, trend } = compareDiff(c24, Math.round(prev?.gold_24_carat / 10));
    rates.push({
      date: curr?.added_on,
      carat18: Math.round(curr?.gold_18_carat / 10),
      carat22: Math.round(curr?.gold_22_carat / 10),
      carat24: c24,
      difference: diff,
      trend,
    });
  }

  let maxRate = -Infinity, minRate = Infinity;
  for (const item of daysData) {
    const v = Math.round(+item?.gold_24_carat / 10);
    if (v > maxRate) maxRate = v;
    if (v < minRate) minRate = v;
  }

  const { diff, trend } = compareDiff(
    Math.round(daysData[0]?.gold_24_carat / 10),
    Math.round(daysData[daysData?.length - 1]?.gold_24_carat / 10)
  );

  return { rates, maxRate, minRate, difference: diff, trend };
}

function extractSilverDaysData(data, days = 7) {
  if (!data || !data.length) return null;
  const daysData = data.slice(0, days + 1);

  const rates = [];
  for (let i = 0; i < daysData.length - 1; i++) {
    const curr = daysData[i];
    const prev = daysData[i + 1];
    const { diff, trend } = compareDiff(curr?.silver_10_gram, prev?.silver_10_gram);
    rates.push({
      date: curr?.added_on,
      oneGram: Math.round(curr?.silver_10_gram / 10),
      tenGram: curr?.silver_10_gram,
      hundredGram: curr?.silver_100_gram,
      oneKg: curr?.silver_1_kg,
      difference: diff,
      trend,
    });
  }

  let maxRate = -Infinity, minRate = Infinity;
  for (const item of daysData) {
    const v = +item?.silver_10_gram;
    if (v > maxRate) maxRate = v;
    if (v < minRate) minRate = v;
  }

  const { diff, trend } = compareDiff(
    daysData[0]?.silver_10_gram,
    daysData[daysData?.length - 1]?.silver_10_gram
  );

  return { rates, maxRate, minRate, difference: diff, trend };
}

export const parseGoldData = (data) => {
  if (!data || !data?.content || !data?.content?.last_ten_day_data || !data?.content?.all_content) return null;
  const rawTenDayData = data?.content?.last_ten_day_data || [];
  const rawData = data?.content?.all_content || [];
  const currentDayData = rawTenDayData?.current_date_price || {};
  const yesterDayData = rawTenDayData?.previous_date_price || {};

  const per10 = (v) => Math.round(v / 10);

  const currentDiffs = {
    carat14: compareDiff(per10(currentDayData?.current_price_gold_14_carat), per10(yesterDayData?.previous_price_gold_14_carat)),
    carat18: compareDiff(per10(currentDayData?.current_price_gold_18_carat), per10(yesterDayData?.previous_price_gold_18_carat)),
    carat22: compareDiff(per10(currentDayData?.current_price_gold_22_carat), per10(yesterDayData?.previous_price_gold_22_carat)),
  };

  const parsed = {};
  parsed.currentRates = {
    oneGram: {
      carat14: per10(currentDayData?.current_price_gold_14_carat),
      carat18: per10(currentDayData?.current_price_gold_18_carat),
      carat22: per10(currentDayData?.current_price_gold_22_carat),
      carat24: per10(currentDayData?.current_price_gold_24_carat),
    },
    eightGram: {
      carat14: 8 * per10(currentDayData?.current_price_gold_14_carat),
      carat18: 8 * per10(currentDayData?.current_price_gold_18_carat),
      carat22: 8 * per10(currentDayData?.current_price_gold_22_carat),
      carat24: 8 * per10(currentDayData?.current_price_gold_24_carat),
    },
    tenGram: {
      carat14: currentDayData?.current_price_gold_14_carat || null,
      carat18: currentDayData?.current_price_gold_18_carat || null,
      carat22: currentDayData?.current_price_gold_22_carat || null,
      carat24: currentDayData?.current_price_gold_24_carat || null,
    },
    difference14: per10(currentDiffs?.carat14?.diff),
    trend14: currentDiffs?.carat14?.trend,
    difference18: per10(currentDiffs?.carat18?.diff),
    trend18: currentDiffs?.carat18?.trend,
    difference22: per10(currentDiffs?.carat22?.diff),
    trend22: currentDiffs?.carat22?.trend,
    difference24: per10(rawTenDayData?.current_previous_difference),
    trend24: rawTenDayData?.current_previous_trend || null,
  };

  const goldTenDayHistory = rawTenDayData?.history || [];
  const goldExtraDay = findDayAfter(rawData, goldTenDayHistory[goldTenDayHistory.length - 1]?.current_date)
    || rawData?.[goldTenDayHistory.length]
    || null;

  parsed.tenDayHistory = {
    rates: [
      ...goldTenDayHistory.map((h) => ({
        date: h?.current_date,
        carat14: per10(h?.current_price_gold_14_carat),
        carat18: per10(h?.current_price_gold_18_carat),
        carat22: per10(h?.current_price_gold_22_carat),
        carat24: per10(h?.current_price_gold_24_carat),
        difference: per10(h?.difference_price_gold_24_carat),
        trend: h?.trend,
      })),
      ...(goldExtraDay ? [{
        date: goldExtraDay?.added_on,
        carat14: goldExtraDay?.gold_14_carat != null
          ? Math.round(goldExtraDay.gold_14_carat / 10)
          : (goldExtraDay?.gold_24_carat != null ? Math.round(Math.round(goldExtraDay.gold_24_carat / 10) * 14 / 24) : null),
        carat18: goldExtraDay?.gold_18_carat != null ? Math.round(goldExtraDay.gold_18_carat / 10) : null,
        carat22: goldExtraDay?.gold_22_carat != null ? Math.round(goldExtraDay.gold_22_carat / 10) : null,
        carat24: goldExtraDay?.gold_24_carat != null ? Math.round(goldExtraDay.gold_24_carat / 10) : null,
        hidden: true,
      }] : []),
    ],
    difference: rawTenDayData?.average_difference || null,
    trend: rawTenDayData?.average_trend || null,
    maxRate: rawTenDayData?.max_10days?.price || null,
    minRate: rawTenDayData?.min_10days?.price || null,
  };

  parsed.sevenDaysHistory = extractGoldDaysData(rawData, 7);
  parsed.thirtyDaysHistory = extractGoldDaysData(rawData, 30);

  parsed.oneYearHistory = extractGoldDaysData(rawData, 366);
  return parsed;
};

export const parseSilverData = (data) => {
  if (!data || !data?.content || !data?.content.last_ten_day_data || !data?.content.all_content) return null;
  const rawTenDayData = data?.content?.last_ten_day_data || [];
  const rawData = data?.content?.all_content || [];
  const currentDayData = rawTenDayData?.current_date_price || {};
  const yesterDayData = rawTenDayData?.previous_date_price || {};

  const currentDiffs = {
    oneGram: compareDiff(currentDayData?.current_price_silver_1_gram, yesterDayData?.previous_price_silver_1_gram) || null,
    tenGram: compareDiff(currentDayData?.current_price_silver_10_gram, yesterDayData?.previous_price_silver_10_gram) || null,
    hundredGram: compareDiff(currentDayData?.current_price_silver_100_gram, yesterDayData?.previous_price_silver_100_gram) || null,
  };

  const parsed = {};
  parsed.currentRates = {
    oneGram: currentDayData?.current_price_silver_1_gram || null,
    tenGram: currentDayData?.current_price_silver_10_gram || null,
    hundredGram: currentDayData?.current_price_silver_100_gram || null,
    oneKg: currentDayData?.current_price_silver_1_kg || null,
    differenceOneGram: currentDiffs?.oneGram.diff || null,
    trendOneGram: currentDiffs?.oneGram.trend || null,
    differenceTenGram: currentDiffs?.tenGram.diff || null,
    trendTenGram: currentDiffs?.tenGram.trend || null,
    differenceHundredGram: currentDiffs?.hundredGram.diff || null,
    trendHundredGram: currentDiffs?.hundredGram.trend || null,
    differenceOneKg: rawTenDayData?.current_previous_difference || null,
    trendOneKg: rawTenDayData?.current_previous_trend || null,
  };

  const silverTenDayHistory = rawTenDayData?.history || [];
  const silverExtraDay = findDayAfter(rawData, silverTenDayHistory[silverTenDayHistory.length - 1]?.current_date)
    || rawData?.[silverTenDayHistory.length]
    || null;

  parsed.tenDayHistory = {
    rates: [
      ...silverTenDayHistory.map((h) => ({
        date: h?.current_date,
        oneGram: h?.current_price_silver_1_gram || null,
        tenGram: h?.current_price_silver_10_gram || null,
        hundredGram: h?.current_price_silver_100_gram || null,
        oneKg: h?.current_price_silver_1_kg || null,
        difference: h?.difference_price_silver_1_kg || null,
        trend: h?.trend || null,
      })),
      // Extra prior-day row used only to compute the trend for the last displayed row; not rendered.
      ...(silverExtraDay ? [{
        date: silverExtraDay?.added_on,
        oneGram: silverExtraDay?.silver_10_gram != null ? Math.round(silverExtraDay.silver_10_gram / 10) : null,
        tenGram: silverExtraDay?.silver_10_gram ?? null,
        hundredGram: silverExtraDay?.silver_100_gram ?? null,
        oneKg: silverExtraDay?.silver_1_kg ?? null,
        hidden: true,
      }] : []),
    ],
    difference: rawTenDayData?.average_difference || null,
    trend: rawTenDayData?.average_trend || null,
    maxRate: rawTenDayData?.max_10days?.price || null,
    minRate: rawTenDayData?.min_10days?.price || null ,
  };

  parsed.sevenDaysHistory = extractSilverDaysData(rawData, 7);
  parsed.thirtyDaysHistory = extractSilverDaysData(rawData, 30);

  parsed.oneYearHistory = extractSilverDaysData(rawData, 366);
  return parsed;
};

export const parseGoldMultiCityData = (data, stateSlug = "uttar-pradesh") => {
  if (!data || !data?.content || !data?.content.state_city_list || !data?.content?.state_city_list?.state_city_list?.[stateSlug]) return null;
  const stateData = Object.values(data?.content?.state_city_list?.state_city_list?.[stateSlug]?.citylist);
  const cityData = stateData?.map((c) => ({
    city: c.city,
    carat18: formatINR(Math.round(c?.gold_18_carat / 10)),
    carat22: formatINR(Math.round(c?.gold_22_carat / 10)),
    carat24: formatINR(Math.round(c?.gold_24_carat / 10)),
    carat14: formatINR(Math.round(c?.gold_14_carat / 10)),
  }));
  return cityData;
}

export const parseSilverMultiCityData = (data, stateSlug = "uttar-pradesh") => {
  if (!data || !data?.content || !data?.content.state_city_list || !data?.content?.state_city_list?.state_city_list?.[stateSlug]) return null;
  const stateData = Object.values(data?.content?.state_city_list?.state_city_list?.[stateSlug]?.citylist);
  const cityData = stateData?.map((c) => ({
    city: c.city,
    oneGram: formatINR(c?.silver_1_gram),
    tenGram: formatINR(c?.silver_10_gram),
    hundredGram: formatINR(c?.silver_100_gram),
    oneKg: formatINR(c?.silver_1_kg),
  }));
  return cityData;
}

const extractGoldOverview = (data, citySlug = "uttar-pradesh") => {
  if (!data || !data?.content ) return null;
  const rawTenDayData = data?.content?.last_ten_day_data || {};
  const currentDayData = rawTenDayData?.current_date_price || {};

  const per10 = (v) => Math.round(v / 10);

  return {
    oneGram: {
     carat24: per10(currentDayData?.current_price_gold_24_carat),
    },
    eightGram: {
      carat24: 8 * per10(currentDayData?.current_price_gold_24_carat),
    },
    tenGram: {
      carat24: currentDayData?.current_price_gold_24_carat || null,
    },
    difference24: per10(rawTenDayData?.current_previous_difference),
    trend24: rawTenDayData?.current_previous_trend || null,
    pageHref: process.env.NEXT_PUBLIC_WEBSITE_URL + `/gold-rates/${citySlug}-gold-price-today`
  };
};

const extractSilverOverview = (data, citySlug = "uttar-pradesh") => {
  if (!data || !data?.content || !data?.content?.last_ten_day_data) return null;
  const rawTenDayData = data?.content?.last_ten_day_data || {};
  const currentDayData = rawTenDayData?.current_date_price || {};
  const yesterDayData = rawTenDayData?.previous_date_price || {};

  const currentDiffs = {
    oneGram: compareDiff(currentDayData?.current_price_silver_1_gram, yesterDayData?.previous_price_silver_1_gram),
    tenGram: compareDiff(currentDayData?.current_price_silver_10_gram, yesterDayData?.previous_price_silver_10_gram),
    hundredGram: compareDiff(currentDayData?.current_price_silver_100_gram, yesterDayData?.previous_price_silver_100_gram),
  };

  return {
    oneGram: currentDayData?.current_price_silver_1_gram || null,
    tenGram: currentDayData?.current_price_silver_10_gram || null,
    hundredGram: currentDayData?.current_price_silver_100_gram || null,
    oneKg: currentDayData?.current_price_silver_1_kg || null,
    differenceOneGram: currentDiffs?.oneGram?.diff || null,
    trendOneGram: currentDiffs?.oneGram?.trend || null,
    differenceTenGram: currentDiffs?.tenGram?.diff || null,
    trendTenGram: currentDiffs?.tenGram?.trend || null,
    differenceHundredGram: currentDiffs?.hundredGram?.diff || null,
    trendHundredGram: currentDiffs?.hundredGram?.trend || null,
    differenceOneKg: rawTenDayData?.current_previous_difference || null,
    trendOneKg: rawTenDayData?.current_previous_trend || null,
    pageHref: process.env.NEXT_PUBLIC_WEBSITE_URL + `/silver-rates/${citySlug}-silver-price-today`
  };
};

export const parseGoldSilverOverview = (goldData, silverData, citySlug = "uttar-pradesh") => {
  const goldOverview = extractGoldOverview(goldData, citySlug);
  const silverOverview = extractSilverOverview(silverData, citySlug);
  if (!goldOverview || !silverOverview) return null;
  return {
    gold: goldOverview,
    silver: silverOverview,
  };
};

const normalizeCityName = (str = "") => str?.toLowerCase()?.replace(/[-\s_]+/g, " ")?.trim();

// When stateSlug is given, reads that state's citylist bucket only. Otherwise
// (nationwide view) merges every state's citylist so each city's price comes
// from its own state, rather than defaulting to a single hardcoded state.
// Keyed by each entry's own `city` field (normalized) rather than the dict's
// key, since the citylist's keys don't reliably match the plain city name
// for multi-word cities (e.g. "Sawai Madhopur").
const buildCityList = (statesObj = {}, stateSlug) => {
  const citylists = stateSlug
    ? [statesObj?.[stateSlug]?.citylist]
    : Object.values(statesObj).map((state) => state?.citylist);

  const map = {};
  citylists.forEach((citylist) => {
    Object.values(citylist || {}).forEach((entry) => {
      if (entry?.city) map[normalizeCityName(entry.city)] = entry;
    });
  });
  return map;
};

export const parseGoldSilverMultiCityOverview = (goldMultiData, silverMultiData, cities = [], stateSlug = null) => {
  const goldStates = goldMultiData?.content?.state_city_list?.state_city_list || {};
  const silverStates = silverMultiData?.content?.state_city_list?.state_city_list || {};
  const goldCityList = buildCityList(goldStates, stateSlug);
  const silverCityList = buildCityList(silverStates, stateSlug);

  return cities.map((city) => {
    const key = normalizeCityName(city);
    const goldCity = goldCityList?.[key];
    const silverCity = silverCityList?.[key];
    return {
      city,
      gold: goldCity ? Math.round(goldCity.gold_24_carat/10) : null,
      silver: silverCity ? (silverCity.silver_10_gram ?? null) : null,
    };
  });
};

// Upstream gold/silver prices are only published per-city, so each state's
// price is taken from its representative city (see getGoldSilverStates).
export const parseGoldSilverMultiStateOverview = (goldMultiData, silverMultiData, states = []) => {
  const goldStates = goldMultiData?.content?.state_city_list?.state_city_list || {};
  const silverStates = silverMultiData?.content?.state_city_list?.state_city_list || {};

  return states.map(({ state, state_hn, state_url, representativeCity }) => {
    const goldCityList = buildCityList(goldStates, state_url);
    const silverCityList = buildCityList(silverStates, state_url);
    const key = normalizeCityName(representativeCity);
    const goldCity = goldCityList?.[key];
    const silverCity = silverCityList?.[key];
    return {
      state,
      state_hn,
      state_url,
      gold: goldCity ? Math.round(goldCity.gold_24_carat / 10) : null,
      silver: silverCity ? (silverCity.silver_10_gram ?? null) : null,
    };
  });
};
